import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { get } from "../../../services/lewooApi";
import { StatCard, PageHeader, LoadingScreen } from "../../../components/admin/EssUi";
import { Search, Download } from "lucide-react";

const ROWS_PER_PAGE = 10;

const formatCurrency = (val) => {
  if (val === null || val === undefined) return "—";
  return "$" + Number(val).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const mapState = (state) => {
  const s = {
    draft: "Draft",
    confirm: "Waiting",
    done: "Sent",
    paid: "Paid",
    cancel: "Cancelled",
  };
  return s[state] || state || "—";
};

const AdminPanelPayslipsPage = () => {
  const intl = useIntl();
  const { permissions } = useSelector((state) => state.auth) || {};
  const canManage = !!permissions?.canManagePayslips;
  const [data, setData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    (async () => {
      try {
        const [payslipRes, empRes] = await Promise.all([
          get("/api/payslips"),
          get("/api/employees?limit=500"),
        ]);
        const payslipRows = payslipRes?.data || [];
        const empRows = empRes?.data || [];
        const empMap = {};
        empRows.forEach((e) => { empMap[e.id] = e; });

        setData((payslipRows || []).map((e, i) => ({
          id: e.id || i,
          name: e.name || "—",
          employee: (empMap[e.employee_id] || {}).name || "—",
          idNumber: e.number || "—",
          period: e.date_from && e.date_to
            ? new Date(e.date_from).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })
            : "—",
          basic: formatCurrency(e.wage || e.basic),
          allowances: formatCurrency(e.allowance_total || e.total_allowance || 0),
          deductions: formatCurrency(e.deduction_total || e.total_deduction || e.deductions || 0),
          net: formatCurrency(e.net_total || e.net_wage || e.net || 0),
          department: (empMap[e.employee_id] || {}).department || "—",
          status: mapState(e.state),
        })));
      } catch { setData([]); }
      setLoaded(true);
    })();
  }, []);

  if (!loaded) return <LoadingScreen />;

  const depts = [...new Set(data.map(r => r.department))].filter(d => d !== "—");
  const statuses = [...new Set(data.map(r => r.status))];

  const getTabFilter = () => {
    if (activeTab === "sent") return { key: "status", value: "Sent" };
    if (activeTab === "draft") return { key: "status", value: "Draft" };
    if (activeTab === "validated") return { key: "status", value: "Validated" };
    return null;
  };
  const tabFilter = getTabFilter();

  const filtered = data.filter(r => {
    if (tabFilter && r.status !== tabFilter.value) return false;
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (deptFilter !== "all" && r.department !== deptFilter) return false;
    if (search.trim() && !r.employee.toLowerCase().includes(search.toLowerCase()) && !r.idNumber.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / ROWS_PER_PAGE));
  const paginatedData = filtered.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);

  const tabs = [
    { key: "all", label: "All Payslips", count: data.length },
    { key: "sent", label: "Sent", count: data.filter(r => r.status === "Sent").length },
    { key: "validated", label: "Validated", count: data.filter(r => r.status === "Validated").length },
    { key: "draft", label: "Draft", count: data.filter(r => r.status === "Draft").length },
  ];

  const getStatusColor = (status) => {
    const colors = { "Sent": "#16a34a", "Validated": "#4f46e5", "Draft": "#d97706", "Paid": "#0891b2", "Waiting": "#7c3aed" };
    return colors[status] || "#64748b";
  };

  const getStatusBg = (status) => {
    const colors = { "Sent": "#dcfce7", "Validated": "#eef2ff", "Draft": "#fef3c7", "Paid": "#cffafe", "Waiting": "#f3e8ff" };
    return colors[status] || "#f1f5f9";
  };

  const netAmounts = data.map(r => parseInt(r.net.replace(/[^0-9]/g, "")) || 0);
  const grossAmounts = data.map(r => {
    const basic = parseInt(r.basic.replace(/[^0-9]/g, "")) || 0;
    const allData = parseInt(r.allowances.replace(/[^0-9]/g, "")) || 0;
    return basic + allData;
  });
  const sentCount = data.filter(r => r.status === "Sent").length;
  const avgNet = netAmounts.length ? Math.round(netAmounts.reduce((s, n) => s + n, 0) / netAmounts.length) : 0;

  const payrollSummary = [
    { label: "Gross Payroll", value: formatCurrency(grossAmounts.reduce((s, n) => s + n, 0)) },
    { label: "Total Allowances", value: formatCurrency(data.map(r => parseInt(r.allowances.replace(/[^0-9]/g, "")) || 0).reduce((s, n) => s + n, 0)) },
    { label: "CNPS (Employer)", value: formatCurrency(Math.round(grossAmounts.reduce((s, n) => s + n, 0) * 0.0942)) },
    { label: "Income Tax Withheld", value: formatCurrency(Math.round(grossAmounts.reduce((s, n) => s + n, 0) * 0.12)) },
    { label: "Net Payroll", value: formatCurrency(netAmounts.reduce((s, n) => s + n, 0)) },
    { label: "Avg Net Salary", value: formatCurrency(avgNet) },
    { label: "Payslips Sent", value: sentCount.toString() },
  ];

  const monthlyData = {};
  data.forEach(r => {
    if (r.period && r.period !== "—") {
      const date = new Date(r.period);
      if (!isNaN(date.getTime())) {
        const m = date.toLocaleDateString('en-US', { month: 'short' });
        const netNum = parseInt(r.net.replace(/[^0-9]/g, "")) || 0;
        monthlyData[m] = (monthlyData[m] || 0) + netNum;
      }
    }
  });
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const payrollTrend = months.map(m => ({ month: m, amount: monthlyData[m] || 0 }));
  const activeMonths = payrollTrend.filter(d => d.amount > 0);
  const trendData = activeMonths.length > 0 ? activeMonths : payrollTrend;
  const maxAmount = Math.max(...trendData.map(d => d.amount), 1);

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: '0 16px' }}>
      <PageHeader
        title="Payslips"
        subtitle="Generate, review and distribute employee payslips"
        actions={
          <div style={{ display: 'flex', gap: '12px' }}>
            {canManage && (
            <button className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-sm" style={{ background: "#059669", color: "white", border: 'none', cursor: 'pointer' }}>
              ＋ Generate Batch
            </button>
            )}
            <button className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all" style={{ background: "#f1f5f9", color: "#334155", border: 'none', cursor: 'pointer' }}>
              <Download size={16} />
              Export All
            </button>
          </div>
        }
      />

      <div className="ess-sg ess-sg4" style={{ marginBottom: '24px' }}>
        <StatCard label="Total Payslips" value={data.length} icon="📄" color="indigo" trend="All time" />
        <StatCard label="Sent" value={data.filter(r => r.status === "Sent").length} icon="✅" color="green" trend="Delivered" />
        <StatCard label="Validated" value={data.filter(r => r.status === "Validated").length} icon="✔️" color="blue" trend="Approved" />
        <StatCard label="Draft" value={data.filter(r => r.status === "Draft").length} icon="📝" color="amber" trend="Pending" />
      </div>

      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '4px', overflowX: 'auto' }}>
        {tabs.map((tab) => (
          <button key={tab.key} onClick={() => { setActiveTab(tab.key); setPage(1); }}
            style={{
              padding: '8px 20px', border: 'none', background: activeTab === tab.key ? '#059669' : 'transparent',
              color: activeTab === tab.key ? 'white' : '#475569', borderRadius: '8px', fontSize: '13px',
              fontWeight: activeTab === tab.key ? '600' : '500', cursor: 'pointer', transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap'
            }}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span style={{ background: activeTab === tab.key ? 'rgba(255,255,255,0.2)' : '#f1f5f9', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600' }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '24px', background: 'white', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
        <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#94a3b8' }} />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search employee, ID..."
            style={{ width: '100%', padding: '10px 12px 10px 36px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', background: '#f8fafc' }}
            onFocus={(e) => e.target.style.borderColor = '#4f46e5'}
            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
          />
        </div>
        <select value={deptFilter} onChange={(e) => { setDeptFilter(e.target.value); setPage(1); }}
          style={{ padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', background: '#f8fafc', outline: 'none', cursor: 'pointer', minWidth: '160px' }}
        >
          <option value="all">All Departments</option>
          {depts.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          style={{ padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', background: '#f8fafc', outline: 'none', cursor: 'pointer', minWidth: '160px' }}
        >
          <option value="all">All Status</option>
          {statuses.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div style={{ display: 'grid', gap: '24px' }} className="ess-rg-2-1">
        <div>
          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Employee</th>
                    <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Slip #</th>
                    <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Period</th>
                    <th style={{ textAlign: 'right', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Basic</th>
                    <th style={{ textAlign: 'right', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Allowances</th>
                    <th style={{ textAlign: 'right', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Deductions</th>
                    <th style={{ textAlign: 'right', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Net Pay</th>
                    <th style={{ textAlign: 'left', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
                    <th style={{ textAlign: 'right', padding: '14px 16px', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.length === 0 ? (
                    <tr><td colSpan={9} style={{ padding: '40px 16px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>No payslips found</td></tr>
                  ) : paginatedData.map((row, index) => (
                    <tr key={row.id} style={{ borderBottom: index < paginatedData.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                      <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{row.employee}</td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#94a3b8', fontWeight: '500', whiteSpace: 'nowrap' }}>{row.idNumber}</td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#64748b', whiteSpace: 'nowrap' }}>{row.period}</td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#475569', textAlign: 'right' }}>{row.basic}</td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#475569', textAlign: 'right' }}>{row.allowances}</td>
                      <td style={{ padding: '14px 16px', fontSize: '13px', color: '#e11d48', textAlign: 'right' }}>{row.deductions}</td>
                      <td style={{ padding: '14px 16px', fontSize: '14px', fontWeight: '700', color: '#16a34a', textAlign: 'right' }}>{row.net}</td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ background: getStatusBg(row.status), color: getStatusColor(row.status), padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>{row.status}</span>
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                        <button style={{ border: 'none', background: 'transparent', color: '#4f46e5', fontSize: '13px', fontWeight: '500', cursor: 'pointer', padding: '4px 8px', borderRadius: '4px' }}
                          onMouseEnter={(e) => e.target.style.background = '#eef2ff'}
                          onMouseLeave={(e) => e.target.style.background = 'transparent'}
                        >View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '16px' }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                style={{ padding: '8px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', background: page === 1 ? '#f8fafc' : 'white', color: page === 1 ? '#94a3b8' : '#059669', cursor: page === 1 ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '500' }}
              >← Previous</button>
              <span style={{ fontSize: '13px', color: '#64748b' }}>Page {page} of {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                style={{ padding: '8px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', background: page === totalPages ? '#f8fafc' : 'white', color: page === totalPages ? '#94a3b8' : '#059669', cursor: page === totalPages ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: '500' }}
              >Next →</button>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>Payroll Summary</h3>
            {payrollSummary.map((item) => (
              <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: '13px', color: '#64748b' }}>{item.label}</span>
                <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>{item.value}</span>
              </div>
            ))}
          </div>

          <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1e293b', marginBottom: '16px' }}>Payroll Cost Trend</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', height: '100px', gap: '8px' }}>
              {trendData.map((item) => {
                const height = (item.amount / maxAmount) * 100;
                return (
                  <div key={item.month} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                    <div style={{ width: '100%', height: `${height}px`, background: item.month === 'Jun' ? '#4f46e5' : '#818cf8', borderRadius: '4px 4px 0 0', minHeight: '4px', transition: 'height 0.5s ease', opacity: item.month === 'Jun' ? 1 : 0.6 }} />
                    <div style={{ fontSize: '10px', color: '#64748b', marginTop: '6px', fontWeight: '500' }}>{item.month}</div>
                    <div style={{ fontSize: '10px', fontWeight: '600', color: '#1e293b' }}>${(item.amount / 1000).toFixed(0)}k</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanelPayslipsPage;
